import {
  AvatarDropdown,
  AvatarName,
  Fullscreen,
  PendingTasks,
  SelectLang,
  // Question,
  TimezoneSelector,
} from '@/components';

// import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';

import { SkinOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings, ProSettings } from '@ant-design/pro-components';
import { PageContainer, SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { getIntl, getLocale, history } from '@umijs/max';
// 引入ConfigProvider
import type { ThemeConfig } from 'antd';
import { ConfigProvider, message, notification, Typography, theme } from 'antd';
import { useEffect, useRef } from 'react';
import KeepAlive, { AliveScope, useActivate, useAliveController } from 'react-activation';
import { errorConfig } from '@/services/api/requestErrorConfig';
import defaultSettings from '../config/defaultSettings';
import routes from '../config/routes';
import './app.scss';
import { getUserMsg } from './services/api/User';
import { useUserStore } from './store';
import { getUserFlatMenus, getUserTreeMenus, setUserData } from './store/user';
import { convertFlatMenusToTree, convertTreeToFlatMenus } from './utils/util';

const { Text } = Typography;
const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

const BASE_URL = process.env.BASE_URL || '/admin';

// ==================== 静态 message / notification 主题 ====================
/*
 * 让静态调用的 message / notification / Modal 跟随换肤。
 *
 * antd v5 里 `message.success()` 这类静态方法不在 React 组件树内，会被渲染到一个独立的
 * render root 上，拿不到外层 ConfigProvider 的上下文 —— 于是主题色、darkAlgorithm、
 * fontFamily 全部失效，深色模式下仍是浅色提示条。`ConfigProvider.config` 是官方给静态方法
 * 指定主题/容器的入口，在这里一次性覆盖全项目的静态调用（含请求拦截器里的 message.error /
 * message.success），无需逐页改成 App.useApp()。
 *
 * ⚠️ 必须在【任何静态调用之前】就把 holderRender 注册好，之后只改主题、不改它包出来的层级：
 * antd 只在 holder 第一次挂载时记住实例（源码 `if (!newMessage.instance && instance)`），
 * 如果先弹过提示（holder 已按「无 holderRender」的结构挂过一次），再去 config 一个
 * holderRender，React 会因为多了一层 ConfigProvider 而把 holder 卸载重挂，antd 却仍持有
 * 旧实例 —— 之后所有静态提示都打在已卸载的实例上，表现为「全局的操作成功/失败没有提示」。
 * 典型触发路径：进页面时 token 已过期，getInitialState 阶段先弹了一次错误提示，
 * 随后 layout 挂载才注册 holderRender，此后整个会话都不再有任何提示。
 */
/** 读取本地缓存的皮肤，保证 layout 挂载前弹出的提示（如 token 过期）主题也正确 */
const getSavedThemeSettings = (): {
  colorPrimary?: string;
  isDarkMode?: boolean;
} => {
  try {
    return JSON.parse(localStorage.getItem('app-settings') || '{}') || {};
  } catch {
    return {};
  }
};

const buildStaticTheme = (colorPrimary?: string, isDarkMode?: boolean): ThemeConfig => ({
  cssVar: true,
  token: {
    colorPrimary,
    fontFamily: 'AlibabaSans, sans-serif',
  },
  algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
});

const savedThemeSettings = getSavedThemeSettings();

let staticTheme: ThemeConfig = buildStaticTheme(
  savedThemeSettings.colorPrimary,
  savedThemeSettings.isDarkMode,
);

// 模块加载即注册（早于任何静态 message 调用），之后不再改动 holderRender 本身
ConfigProvider.config({
  theme: staticTheme,
  holderRender: (children) => <ConfigProvider theme={staticTheme}>{children}</ConfigProvider>,
});

const applyStaticTheme = (colorPrimary?: string, isDarkMode?: boolean) => {
  staticTheme = buildStaticTheme(colorPrimary, isDarkMode);
  // 只更新主题，不再传 holderRender：holder 的层级结构保持不变，不会卸载重挂
  ConfigProvider.config({ theme: staticTheme });
  // 让已挂载的 holder 重新渲染，重新执行 holderRender 拿到新的 staticTheme
  message.config({});
  notification.config({});
};

// 小屏幕（宽度小于断点）默认收起左侧菜单
const SMALL_SCREEN_WIDTH = 1920;
const isSmallScreen = () => typeof window !== 'undefined' && window.innerWidth < SMALL_SCREEN_WIDTH;

// 监听窗口尺寸变化，跨越断点时自动收起/展开左侧菜单（只在小屏<->大屏切换时触发，
// 保留用户在同一尺寸区间内的手动展开/收起）
let latestSetInitialState: ((updater: (pre: any) => any) => void) | null = null;
let resizeBound = false;
const bindMenuResize = () => {
  if (resizeBound || typeof window === 'undefined') return;
  resizeBound = true;
  let prevSmall = isSmallScreen();
  window.addEventListener('resize', () => {
    const small = isSmallScreen();
    if (small === prevSmall) return;
    prevSmall = small;
    latestSetInitialState?.((pre: any) => ({ ...pre, collapsed: small }));
  });
};

// keep-alive：drop 方法由 AliveControllerBridge 注入，关闭页签时清理页面缓存
let aliveDrop: ((name: string) => void) | null = null;

// 去掉路径末尾多余的斜杠（根路径 "/" 除外）。
// 刷新时 URL 可能多出尾斜杠（如 /memberManage/list/），会导致：
// 1) 与菜单 url 不匹配被判无权限而跳转到第一个菜单；2) 页签 key 不同而重复。
const stripTrailingSlash = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

// 版本号，用来识别是否清除用户浏览器缓存；构建时自动注入构建时间(config.ts)
const SETTINGS_VERSION = process.env.SETTINGS_VERSION || '1.0.0';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
  isDrawerVisible?: boolean;
  havePermissionList?: Array<any>;
  agentData?: AgentData;
  removeTab?: (e: any, action: any) => void;
  collapsed?: boolean;
}> {
  const token = useUserStore.getState().token;

  const fetchUserInfo = async () => {
    try {
      const res = await getUserMsg();
      if (res?.code === '200' && res.data) {
        const resData = {
          user: {},
          userTreeMenus: res.data,
          imgCommonUrl: '',
        };
        setUserData(resData); // 本地存储用户数据和菜单（包括计算 userFlatMenus）
        return {
          userTreeMenus: res.data,
          permsList: [],
          agentData: { isProxyList: false, agentAuthorityControlList: [] },
        };
      }
      // TODO: logout api
      useUserStore.getState().removeToken();
    } catch {
      history.push(loginPath);
      return undefined;
    }
    return undefined;
  };

  const initData = async () => {
    // 请求字典
    await Promise.all([]);
  };

  const savedSettings = localStorage.getItem('app-settings');

  let parsedSettings = savedSettings ? JSON.parse(savedSettings) : null;

  // 如果版本号不匹配，说明是旧数据，需要清除
  if (parsedSettings?.settingsVersion !== SETTINGS_VERSION) {
    localStorage.removeItem('app-settings');
    parsedSettings = null;
  }

  // 有缓存优先读取缓存配置
  const newDefaultSettings = parsedSettings
    ? {
        ...parsedSettings,
        isDarkMode: parsedSettings.isDarkMode ?? false,
      }
    : {
        ...defaultSettings,
        colorPrimary: localStorage.getItem('colorPrimary') || defaultSettings.colorPrimary,
      };

  // 如果不是登录页面，执行
  const { location } = history;

  if (location.pathname !== loginPath && token) {
    await initData();
  }

  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();

    return {
      fetchUserInfo,
      currentUser,
      settings: newDefaultSettings as Partial<LayoutSettings>,
      isDrawerVisible: false,
      havePermissionList: currentUser?.permsList,
      agentData: currentUser?.agentData || {
        isProxyList: false,
        agentAuthorityControlList: [],
      },
      collapsed: isSmallScreen(),
    };
  }

  if (location.pathname === loginPath && token) {
    const currentUser = await fetchUserInfo();
    if (currentUser) {
      const urlParams = new URL(window.location.href).searchParams;
      const redirect = urlParams.get('redirect') || '/';
      history.push(redirect);
      return {
        fetchUserInfo,
        currentUser,
        settings: newDefaultSettings as Partial<LayoutSettings>,
        isDrawerVisible: false,
        havePermissionList: currentUser?.permsList,
        agentData: currentUser?.agentData || {
          isProxyList: false,
          agentAuthorityControlList: [],
        },
        collapsed: isSmallScreen(),
      };
    }
  }

  return {
    fetchUserInfo,
    settings: newDefaultSettings as Partial<LayoutSettings>,
    isDrawerVisible: false,
  };
}

// 页签持久化：刷新后仍保留已打开的页签
const TAB_STORAGE_KEY = 'app-tab-list';
function loadTabs(): Array<TabType> {
  try {
    const raw = sessionStorage.getItem(TAB_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function persistTabs() {
  try {
    sessionStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(tabList));
  } catch {
    // ignore
  }
}

let tabList: Array<TabType> = loadTabs();

interface CustomProSettings extends ProSettings {
  isDarkMode?: boolean;
}

const toggleDrawerVisibility = (_initialState: any, setInitialState: any) => {
  setInitialState((pre: any) => ({
    ...pre,
    isDrawerVisible: !pre.isDrawerVisible,
  }));
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const intl = getIntl(getLocale());
  const getLang = () =>
    localStorage.getItem('lang') || localStorage.getItem('defaultLang') || 'zh_CN';

  // 页签名称翻译，与左侧菜单保持一致：中文用 menuName，英文由 url 推导
  const getMenuLabel = (userMenu: any, fallback: string) => {
    if (!userMenu) return fallback;
    if (getLang() === 'zh_CN') return userMenu.menuName || fallback;
    return (
      userMenu.url
        ?.split('/') // 1. 按斜杠拆分
        .pop() // 2. 取最后一段 (如 memberList)
        .replace(/([A-Z])/g, ' $1') // 3. 在大写字母前加空格 (member List)
        .trim() // 4. 去掉前后空格
        .replace(/^\S/, (s: any) => s.toUpperCase()) || fallback // 5. 首字母大写
    );
  };

  // 根据路径在路由表中查找 i18n name（用于非菜单页签，如首页）
  const getRouteI18nName = (routeList: any, path: string): string | undefined => {
    for (const route of routeList) {
      if (route.routes && route.routes.length > 0) {
        const found = getRouteI18nName(route.routes, path);
        if (found) return found;
      } else if (route.path === path && route.name) {
        return route.name;
      }
    }
    return undefined;
  };

  let routeName: TabType = { tab: '', key: '', path: '' };
  const getRouteDetails = (routeList: any, pathname: string, userFlatMenus = []) => {
    routeList.forEach((route: any) => {
      if (route.routes && route.routes.length > 0) {
        getRouteDetails(route.routes, pathname, userFlatMenus);
      } else if (
        route.name !== undefined &&
        route.redirect === undefined &&
        pathname.includes(route.path)
      ) {
        const userMenu: any = userFlatMenus.find(
          (item: any) => item?.url?.replace(/\s*/g, '') === route.path,
        );
        routeName = {
          tab: getMenuLabel(userMenu, intl.formatMessage({ id: route.name })),
          key: route.path,
          path: pathname,
        };
      }
    });
  };

  const checkPathnameIsInTabList = (
    // tabList: Array<API.TabType>,
    pathname: string,
    initialState: any,
  ) => {
    if (location.pathname.startsWith('/gameManage/commonTemplate')) {
      return;
    }

    // 平级菜单存于 Zustand store，currentUser 上并没有该字段
    const userFlatMenus = getUserFlatMenus() || [];
    const havePermission = userFlatMenus.some((item: any) => {
      const url = item?.url?.replace(/\s*/g, '');
      return url && pathname.includes(url);
    });
    // 无权限时，不添加tab
    if (!havePermission) {
      return;
    }

    const found = tabList.find((tab: TabType) => pathname.includes(tab.path?.split('?')[0]));
    if (!found) {
      getRouteDetails(routes, pathname, userFlatMenus as any);
      if (
        !(routeName.tab === '' && routeName.key === '' && routeName.path === '') &&
        routeName.path === pathname
      ) {
        tabList.push(routeName);
      }
    } else {
      if (found?.key !== pathname) {
        // if found = true replace that tab to the current path
        const foundIndex = tabList.findIndex((tab: TabType) =>
          pathname.includes(tab.path?.split('?')[0]),
        );
        const filterTabList = tabList;
        getRouteDetails(routes, pathname, userFlatMenus as any);
        filterTabList[foundIndex] = routeName;
        tabList = filterTabList;
      }
    }
  };

  const remove = (e: any, action: any) => {
    const { location } = history;

    if (action === 'remove') {
      const filterTabList = tabList.filter((value: any) => value.key !== e);
      const currentTabPaneIndex = tabList.findIndex((value: any) => value.key === e);

      tabList = filterTabList;

      // 关闭页签时清理该页面的 keep-alive 缓存
      aliveDrop?.(e);

      if (tabList && tabList.length === 1) {
        const addClosableFalseTabList = tabList.map((value: any) => ({
          ...value,
          closable: false,
        }));
        tabList = addClosableFalseTabList;
        history.push(tabList[0].path);
      } else if (tabList && tabList.length > 1) {
        if (location.pathname === e) {
          history.push(tabList?.[currentTabPaneIndex - 1 > -1 ? currentTabPaneIndex - 1 : 0]?.path);
        } else {
          history.push(location.pathname);
        }
      } else {
        history.push('/');
      }
    }
  };

  // 获取父级菜单url
  const getParentPath = (path: string) => {
    const parts = path.split('/');
    // parts.pop();
    return `/${parts[1]}`;
  };

  // 记录最新的 setInitialState 并绑定一次 resize 监听
  latestSetInitialState = setInitialState;
  bindMenuResize();

  return {
    logo: false,
    // 小屏幕默认收起左侧菜单，用户可手动展开/收起
    collapsed: initialState?.collapsed,
    onCollapse: (collapsed: boolean) => {
      setInitialState((pre: any) => ({ ...pre, collapsed }));
    },
    menu: {
      locale: false,
      request: async (_, defaultRouterData) => {
        // 直接从 Zustand store 获取 userFlatMenus
        const userFlatMenus = getUserFlatMenus();
        if (userFlatMenus?.length) {
          const defaultMenuDataAll = convertTreeToFlatMenus(defaultRouterData);

          // 使用map避免嵌套循环
          const userMenuMap = new Map();
          userFlatMenus?.forEach((userMenu: any) => {
            // 筛选出可见的菜单
            if (userMenu.visible === 1) {
              userMenuMap.set(userMenu.url.replace(/\s*/g, ''), true);
            }
          });
          // 路由配置中：目录 path -> 其配置的所有子页面 path。
          // 用于判断带子路由的目录（如“推广渠道”）是否至少有一个可访问的子路由。
          const configChildLeaves = new Map<string, string[]>();
          const collectLeafPaths = (node: any): string[] => {
            if (node?.routes && node.routes.length) {
              const leaves = node.routes.flatMap(collectLeafPaths);
              if (node.path) configChildLeaves.set(node.path, leaves);
              return leaves;
            }
            return node?.component && node?.path ? [node.path] : [];
          };
          routes.forEach(collectLeafPaths);

          // 筛选出 url === path 的菜单；
          // 带子路由的目录：其配置的子路由需至少有一个有权限，否则隐藏该目录，
          // 其余菜单沿用原有 userMenuMap 判断，不做改动。
          const filteredMenus = defaultMenuDataAll.filter((menu: any) => {
            if (!userMenuMap.has(menu.path)) return false;
            const childLeaves = configChildLeaves.get(menu.path);
            if (childLeaves && childLeaves.length) {
              return childLeaves.some((p) => userMenuMap.has(p));
            }
            return true;
          });

          // 将name替换为接口返回的menuName
          const updatedMenus = filteredMenus.map((menu: any) => {
            const userMenu: any = userFlatMenus?.find((item: any) => {
              return item.url.replace(/\s*/g, '') === menu.path;
            });
            // 菜单类型（M目录 C菜单 F按钮）
            if (userMenu && userMenu.menuType !== 'M' && menu.path !== '/') {
              menu.name =
                getLang() === 'zh_CN'
                  ? userMenu.menuName
                  : userMenu.url
                      .split('/') // 1. 按斜杠拆分
                      .pop() // 2. 取最后一段 (如 memberList)
                      .replace(/([A-Z])/g, ' $1') // 3. 在大写字母前加空格 (变成 member List)
                      .trim() // 4. 去掉前后空格
                      .replace(/^\S/, (s: any) => s.toUpperCase()); // 5. 首字母大写 (变成 Member List)
            }
            if (userMenu) {
              menu.orderNum = Number(userMenu.orderNum);
            }
            return menu;
          });
          // 增加排序
          updatedMenus.sort((a: any, b: any) => (a.orderNum < b.orderNum ? -1 : 1));

          const newMenus = convertFlatMenusToTree(updatedMenus);

          // 最外层数据需要加上menuName
          newMenus.forEach((menu) => {
            const userMenu: any = userFlatMenus?.find(
              (item: any) => item.url.replace(/\s*/g, '') === menu.path,
            );
            if (userMenu && menu.path !== '/') {
              menu.name =
                getLang() === 'zh_CN'
                  ? userMenu.menuName
                  : userMenu.url
                      .replace(/^\//, '') // 1. 去掉开头的 /
                      .replace(/([A-Z])/g, ' $1') // 2. 在所有大写字母前加空格
                      .trim() // 3. 去掉首尾多余空格
                      .replace(/^\S/, (s: any) => s.toUpperCase()); // 4. 确保首字母大写
            }
          });
          return newMenus || [];
        }
        return [];
      },
    },
    actionsRender: () => [
      // <Question key="doc" />,
      <TimezoneSelector key="timeZone" />,
      // <Tutorial key="tutorial" />,
      <SelectLang key="SelectLang" />,

      <div
        key="setting"
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 26,
          padding: '0 8px',
        }}
        onClick={() => toggleDrawerVisibility(initialState, setInitialState)}
      >
        <SkinOutlined />
      </div>,
      <PendingTasks key="pendingTasks" />,
      <Fullscreen key="fullscreen" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 去掉刷新时多出来的尾部斜杠，规范化后再走后续逻辑，
      // 避免误跳转到第一个菜单以及顶部页签重复
      const normalized = stripTrailingSlash(location.pathname);
      if (normalized !== location.pathname) {
        history.replace(`${normalized}${location.search}`);
        return;
      }
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }

      if (!initialState?.removeTab && remove) {
        setInitialState((pre: any) => ({
          ...pre,
          removeTab: remove,
        }));
      }

      const homepagePath = '/welcome';
      // 权限检查
      const userFlatMenus = getUserFlatMenus();
      const userTreeMenus = getUserTreeMenus();
      const havePermission = userFlatMenus.some((item: any) => item.url === location.pathname);
      if (!havePermission && !location.pathname.includes(homepagePath)) {
        const visibleUserMenus = userTreeMenus.filter((userMenu: any) => {
          return userMenu.visible === 1;
        });
        // 获取父级菜单
        const parentMenu = visibleUserMenus.filter((item: any) => {
          const parentPath = getParentPath(location.pathname);
          return parentPath === item.url;
        });
        // 找出第一个有权限的子菜单进行跳转
        if (parentMenu && parentMenu.length > 0) {
          const first = parentMenu[0] || { children: [] };
          const firstHasAuthChild = first.children?.find(
            (item: MenuItem) => item.visible === 1 && item.url && item.url !== '/',
          ) || { url: homepagePath };
          // console.log(firstHasAuthChild, parentMenu, userFlatMenus, location.pathname,  "firstHasAuthChild");
          history.replace(firstHasAuthChild.url as string);
        } else {
          history.back();
          message.error(intl.formatMessage({ id: 'common.noAccessPermission' }));
        }
      }
    },
    bgLayoutImgList: [
      // {
      //   src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
      //   left: 85,
      //   bottom: 100,
      //   height: '303px',
      // },
      // {
      //   src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
      //   bottom: -68,
      //   right: -45,
      //   height: '303px',
      // },
      // {
      //   src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
      //   bottom: 0,
      //   left: 0,
      //   width: '331px',
      // },
    ],
    links: isDev ? [<Text key="version">Arena v1.0.0</Text>] : [],
    menuHeaderRender: undefined,
    // },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;

      checkPathnameIsInTabList(
        `${stripTrailingSlash(location.pathname)}${location.search}`,
        initialState,
      );

      // Translate Tab List (Array) Name according Language
      if (tabList && tabList.length === 1) {
        const addClosableFalseTabList = tabList.map((value: any) => ({
          ...value,
          closable: false,
        }));
        tabList = addClosableFalseTabList;
      }
      if (tabList && tabList.length > 1) {
        const removeClosableFalseTabList = tabList.map((value: any) => ({
          ...value,
          closable: true,
        }));
        tabList = removeClosableFalseTabList;
      }
      // 按当前语言重新翻译页签名称，与左侧菜单保持一致（切换语言时同步更新）
      const flatMenus = getUserFlatMenus() || [];
      const convertedTabList = tabList.map((value: TabType) => {
        const userMenu = flatMenus.find(
          (item: any) => item?.url?.replace(/\s*/g, '') === value.key,
        );
        if (userMenu) {
          return { ...value, tab: getMenuLabel(userMenu, value.tab || '') };
        }
        // 非菜单页签（如首页）按路由 i18n name 翻译
        const i18nName = getRouteI18nName(routes, value.key || '');
        return {
          ...value,
          tab: i18nName ? intl.formatMessage({ id: i18nName }) : value.tab,
        };
      });

      // 持久化页签，刷新后仍保留已打开的页面
      persistTabs();

      // 仅对真实的叶子菜单页（menuType==="C"）启用 keep-alive 缓存；
      // 父级目录/重定向路径（menuType==="M"，如 /bonusManagement）没有实际页面，
      // 平级菜单里同样包含它们，若也套 KeepAlive 会吞掉重定向 <Navigate>、
      // 导致停在父路径显示空白或残留上一个缓存页。这些路径直接渲染 children 让其正常重定向。
      // 统一去掉尾斜杠，避免刷新时的 /x/ 造成缓存 key 与页签不一致
      const currentPath = stripTrailingSlash(location.pathname);
      const isCacheablePage = flatMenus.some(
        (item: any) => item?.menuType !== 'M' && item?.url?.replace(/\s*/g, '') === currentPath,
      );

      const settings = initialState?.settings as CustomProSettings;

      // console.log(theme.defaultAlgorithm, 'theme.defaultAlgorithm');
      return (
        <ConfigProvider
          theme={{
            cssVar: true,
            token: {
              colorPrimary: initialState?.settings?.colorPrimary,
            },
            algorithm: settings?.isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
          }}
        >
          {/* 静态 message / notification 跟随当前皮肤 */}
          <StaticThemeBridge
            colorPrimary={initialState?.settings?.colorPrimary}
            isDarkMode={settings?.isDarkMode}
          />
          {/* AliveScope 放在所有 Context Provider（umi i18n/access、antd ConfigProvider）
              之下、KeepAlive 之上，保证被缓存的页面仍能拿到这些上下文 */}
          <AliveScope>
            {location?.pathname === '/user/login' ? (
              <>{children}</>
            ) : (
              <PageContainer
                className="TabPageContainer"
                title={false}
                breadcrumb={{}}
                tabList={convertedTabList}
                tabProps={{
                  type: 'editable-card',
                  hideAdd: true,
                  onEdit: (e, action) => remove(e, action),
                }}
                tabActiveKey={currentPath}
                onTabChange={(e) => {
                  const findPath = tabList.find((value: any) => value.key === e);
                  history.push(findPath?.path || '');
                }}
              >
                {/* 缓存已打开页面，切换页签时保留状态、不重新请求；
                    父级/重定向路径不缓存，避免快速切换出现残留空白页 */}
                {isCacheablePage ? (
                  <KeepAlive id={currentPath} name={currentPath}>
                    <ActivateRelayout>{children}</ActivateRelayout>
                  </KeepAlive>
                ) : (
                  children
                )}
              </PageContainer>
            )}
            <AliveControllerBridge />
          </AliveScope>

          <SettingDrawer
            disableUrlParams
            enableDarkTheme
            settings={initialState?.settings}
            hideHintAlert={true}
            hideCopyButton={true}
            onCollapseChange={() => {
              setInitialState((prevState) => ({
                ...prevState,
                isDrawerVisible: false,
              }));
            }}
            collapse={initialState?.isDrawerVisible}
            onSettingChange={(settings) => {
              const isDarkMode = settings.navTheme === 'realDark';
              document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');

              // mix模式，需要splitMenus = true
              if (settings.layout === 'mix') {
                settings.splitMenus = true;
              }
              localStorage.setItem(
                'app-settings',
                JSON.stringify({
                  ...settings,
                  isDarkMode,
                  settingsVersion: SETTINGS_VERSION,
                }),
              );
              setInitialState((pre) => ({
                ...pre,
                settings: {
                  ...settings,
                  isDarkMode,
                },
              }));
            }}
          />
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: BASE_URL,
  ...errorConfig,
};

/**
 * keep-alive 控制器桥接：把 drop 方法暴露到模块作用域，
 * 供非组件的 remove()（关闭页签时）清理对应页面缓存。
 * 必须渲染在 AliveScope 内部。
 */
const AliveControllerBridge = () => {
  const { drop } = useAliveController();
  aliveDrop = drop;
  return null;
};

/**
 * 换肤时把最新主题同步给静态调用的 message / notification。
 * 具体机制与「为什么不能在这里注册 holderRender」见文件顶部 applyStaticTheme 的注释。
 */
const StaticThemeBridge: React.FC<{
  colorPrimary?: string;
  isDarkMode?: boolean;
}> = ({ colorPrimary, isDarkMode }) => {
  useEffect(() => {
    applyStaticTheme(colorPrimary, isDarkMode);
  }, [colorPrimary, isDarkMode]);

  return null;
};

/**
 * keep-alive 恢复缓存页时，ProTable 的搜索栏(QueryFilter)通过 RcResizeObserver
 * 监听自身容器宽度来决定筛选项渲染；缓存移动 DOM 后它不会自动重新计算，
 * 导致筛选项消失、只剩“重置/查询”按钮。
 * 这里在页面被激活时，把包裹层宽度微调 1px 再还原，主动触发内部 ResizeObserver 重新布局。
 * 必须渲染在被缓存的子树内部。
 */
const ActivateRelayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useActivate(() => {
    window.dispatchEvent(new Event('resize'));
    const el = ref.current;
    if (!el) return;
    el.style.width = 'calc(100% - 1px)';
    requestAnimationFrame(() => {
      if (ref.current) ref.current.style.width = '100%';
    });
  });
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {children}
    </div>
  );
};
